import {
  Alert,
  IconButton,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGridPremium, type GridColDef } from "@mui/x-data-grid-premium";
import { use, useCallback, useMemo, useState } from "react";
import type { AnyObject } from "@entrilia/shared/types.ts";
import type {
  CashFlowSettingsConfiguration,
  DimensionField,
  MeasureField,
  ReportField,
  TabularRow,
} from "../../../../../../../api/types/svTypes.ts";
import type { IrrCashFlowFrequency } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import { CalculateByField } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import { convertISODateShort } from "@entrilia/shared/utilities/dateUtils";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import IntercomLauncherDependantElement from "@entrilia/shared/components/IntercomLauncherDependantElement.tsx";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import ExpandCollapseButton from "@entrilia/shared/components/ExpandCollapseButton.tsx";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon.tsx";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon.tsx";
import ExpandAllIcon from "@entrilia/shared/icons/ExpandAllIcon.tsx";
import CollapseAllIcon from "@entrilia/shared/icons/CollapseAllIcon.tsx";
import { getMergedSx } from "@entrilia/shared/hooks/useMergedSx.ts";
import type { TabularGridState } from "../../viewer/tabular/hooks/gridState.types.ts";
import { isDimension, isIrr } from "../../../utilities/fieldsHelper.ts";
import { getBiDataGridStyles } from "../../../utilities/Utilities.tsx";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch.ts";
import { selectCurrentReportId } from "../../../store/selectors/currentReportSelectors.ts";
import { createDrillDownPageAction } from "../../../store/utils/storeUtils.ts";
import { createCashFlowDrillDownPage } from "../common/drillDown/irr/cashFlowDrillDown.ts";
import { MeasureCell } from "../../viewer/tabular/utils/measure-column/MeasureCell.tsx";
import ReportLoadingOverlay from "../../common/ReportLoadingOverlay.tsx";
import type { CashFlowMode } from "./cashFlowRows.ts";
import CashFlowScheduleContext from "./CashFlowScheduleContext.ts";
import {
  CASH_FLOW_SPLIT_FIELD_ID,
  buildCashFlowMatrix,
  getCashFlowSeries,
  getCashFlowTotalSeries,
  type CashFlowSeries,
  type CashFlowMatrixRow,
  type CashFlowAmountCell,
} from "./cashFlowMatrix.ts";

const gridSx = {
  "& .MuiDataGrid-row.cash-flow-nav, & .cash-flow-nav .MuiDataGrid-cell--pinnedLeft": { bgcolor: "background.grey" },
};
const gridSlots = { loadingOverlay: ReportLoadingOverlay };
const splitPinnedColumns = { left: ["label", "date"] };
const getCurrencyCode = (field: CalculateByField): string =>
  field === CalculateByField.AdditionalLcy ? "ACY" : field.toUpperCase();
const rowId = (row: CashFlowMatrixRow) => row.id;
const isEmphasized = (row: CashFlowMatrixRow) =>
  row.kind === "Nav" || row.kind === "Irr" || row.kind === "SectionTotal";
const rowClass = ({ row }: { row: CashFlowMatrixRow }) => (isEmphasized(row) ? "cash-flow-nav" : "");

const ExpandAllRowsButton = () => {
  const schedule = use(CashFlowScheduleContext);
  if (!schedule) {
    return null;
  }
  const label = schedule.hasExpandedRows ? "Collapse all" : "Expand all";
  return (
    <Tooltip title={label} arrow enterDelay={500} enterNextDelay={500}>
      <ExpandCollapseButton
        expanded={schedule.hasExpandedRows}
        onToggle={schedule.onToggleAllRows}
        disabled={!schedule.hasExpandableRows}
        aria-label={label}
        aria-expanded={schedule.hasExpandedRows}
        ExpandIcon={ExpandAllIcon}
        CollapseIcon={CollapseAllIcon}
      />
    </Tooltip>
  );
};
const renderExpandAllRowsHeader = () => <ExpandAllRowsButton />;

interface LabelCellProps {
  row: CashFlowMatrixRow;
  showToggle: boolean;
  onToggle: (row: CashFlowMatrixRow) => void;
}

const LabelCell = ({ row, showToggle, onToggle }: LabelCellProps) => (
  <Stack direction="row" sx={{ alignItems: "center" }}>
    {showToggle && row.expandable && (
      <IconButton
        aria-label={row.expanded ? "Collapse transactions" : "Expand transactions"}
        onClick={() => onToggle(row)}
      >
        {row.expanded ? <CollapseIcon /> : <ExpandIcon />}
      </IconButton>
    )}
    <Typography variant={isEmphasized(row) ? "subtitle2" : "body1"}>{row.label}</Typography>
  </Stack>
);

interface AmountCellProps {
  row: CashFlowMatrixRow;
  cell: CashFlowAmountCell;
  value: string;
  hasFocus: boolean;
}

const AmountCell = ({ row, cell, value, hasFocus }: AmountCellProps) => {
  const schedule = use(CashFlowScheduleContext);
  const variant = isEmphasized(row) ? "subtitle2" : "body1";
  return schedule?.canDrillDown && cell.drillDownInfo ? (
    <Link
      component="button"
      color="inherit"
      underline="hover"
      variant={variant}
      tabIndex={hasFocus ? 0 : -1}
      onClick={() => schedule.onAmountClick(row, cell)}
    >
      {value}
    </Link>
  ) : (
    <MeasureCell value={value} variant={variant} />
  );
};

interface ColumnOptions {
  mode: CashFlowMode;
  series: CashFlowSeries[];
  dimensionFields: DimensionField[];
  cashFlowFrequency: IrrCashFlowFrequency | undefined | null;
  onToggleRow: (row: CashFlowMatrixRow) => void;
}

const getColumns = ({
  mode,
  series,
  dimensionFields,
  cashFlowFrequency,
  onToggleRow,
}: ColumnOptions): GridColDef<CashFlowMatrixRow>[] => {
  const definitions: GridColDef<CashFlowMatrixRow>[] = [
    {
      field: "label",
      headerName: "",
      width: 160,
      renderHeader: mode === "Both" ? renderExpandAllRowsHeader : undefined,
      renderCell: ({ row }) => <LabelCell row={row} showToggle={mode === "Both"} onToggle={onToggleRow} />,
    },
    {
      field: "date",
      headerName: "Date",
      width: 125,
      valueFormatter: convertISODateShort,
    },
    ...(mode === "Details" && cashFlowFrequency && cashFlowFrequency !== "Daily"
      ? [
          {
            field: "calculationDate",
            headerName: "IRR date",
            width: 125,
            valueGetter: (_: unknown, row: CashFlowMatrixRow) => row.calculationDate ?? row.date,
            valueFormatter: convertISODateShort,
          },
        ]
      : []),
    ...(mode === "Summary"
      ? []
      : dimensionFields.map((field): GridColDef<CashFlowMatrixRow> => ({
          field: field.config.guid,
          headerName: field.config.customLabel || field.meta.caption,
          minWidth: 140,
          flex: 1,
          valueGetter: (_: unknown, row: CashFlowMatrixRow) => row.dimensions?.[field.config.name] ?? "",
        }))),
    ...series.map((column): GridColDef<CashFlowMatrixRow> => ({
      field: column.id,
      headerName: column.label,
      description: column.description,
      type: "number",
      minWidth: 180,
      flex: 1,
      valueGetter: (_: unknown, row: CashFlowMatrixRow) => row.cells[column.id]?.formattedValue ?? "",
      renderCell: ({ row, hasFocus }) => {
        const cell = row.cells[column.id];
        if (!cell) {
          return null;
        }
        return <AmountCell row={row} cell={cell} value={cell.formattedValue ?? ""} hasFocus={hasFocus} />;
      },
    })),
  ];
  return definitions.map((column) => ({ ...column, sortable: false, filterable: false, disableColumnMenu: true }));
};

interface Props {
  gridState: TabularGridState;
  fields: ReportField[];
  settings: CashFlowSettingsConfiguration;
  displayMode: CashFlowMode;
  onDisplayModeChange: (mode: CashFlowMode) => void;
}

const CashFlowPreview = ({ gridState, fields, settings, displayMode, onDisplayModeChange }: Props) => {
  const irr = fields.find(isIrr);

  if (!irr) {
    return <Alert severity="info">Choose an IRR measure in Fields.</Alert>;
  }
  if (!gridState.configurationValidity.valid) {
    return <Alert severity="info">Complete the report conditions and IRR options.</Alert>;
  }
  if (gridState.error) {
    return <Alert severity="error">{gridState.error}</Alert>;
  }
  if (
    !gridState.loading &&
    gridState.rows.length > 1 &&
    gridState.rows.some((row) => !row.data[CASH_FLOW_SPLIT_FIELD_ID])
  ) {
    return <Alert severity="error">IRR Details reports require one IRR result. Check the report configuration.</Alert>;
  }
  const title = irr.config.customLabel || irr.meta.caption;
  return (
    <Stack spacing={2} sx={{ flex: 1, width: "100%", minWidth: 0, minHeight: 0, overflow: "hidden" }}>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", flexShrink: 0, gap: 1 }}
      >
        <Typography variant="subtitle1">{title}</Typography>
        <ToggleButtonGroup
          exclusive
          value={displayMode}
          onChange={(_, next: CashFlowMode | null) => {
            if (next) {
              onDisplayModeChange(next);
            }
          }}
          aria-label="IRR details view"
        >
          <ToggleButton value="Summary">Summary</ToggleButton>
          <ToggleButton value="Details">Details</ToggleButton>
          <ToggleButton value="Both">Both</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {!gridState.loading && gridState.failedMeasures.length > 0 && (
        <Alert severity="error">Some cash flows could not be calculated. Check the IRR options and conditions.</Alert>
      )}
      {gridState.rows.length > 0 ? (
        <CashFlowSchedule
          data={gridState.rows}
          grandTotals={gridState.grandTotals}
          irr={irr}
          title={title}
          fields={fields}
          settings={settings}
          mode={displayMode}
          loading={gridState.loading}
        />
      ) : gridState.loading ? (
        <InlineLoader />
      ) : (
        <Alert severity="info">No cash flows match these conditions.</Alert>
      )}
    </Stack>
  );
};

interface ScheduleProps {
  data: TabularRow[];
  grandTotals: AnyObject;
  irr: MeasureField;
  title: string;
  fields: ReportField[];
  settings: CashFlowSettingsConfiguration;
  mode: CashFlowMode;
  loading: boolean;
}

const CashFlowSchedule = ({ data, grandTotals, irr, title, fields, settings, mode, loading }: ScheduleProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const reportId = useSelector(selectCurrentReportId);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [expandAllRows, setExpandAllRows] = useState<boolean>();
  const sx = useMemo(() => getMergedSx(theme, getBiDataGridStyles, gridSx), [theme]);
  const series = useMemo(() => {
    const amountLabel = irr.config.calculateByField
      ? `Amount (${getCurrencyCode(irr.config.calculateByField)})`
      : "Amount";
    const columns = getCashFlowSeries(data, irr.config.guid, amountLabel);
    const total = getCashFlowTotalSeries(grandTotals, irr.config.guid);
    const isSplit = data.some((row) => row.data[CASH_FLOW_SPLIT_FIELD_ID]);
    return isSplit && total ? [...columns, total] : columns;
  }, [data, grandTotals, irr.config.guid, irr.config.calculateByField]);
  const rows = useMemo(
    () =>
      buildCashFlowMatrix(
        series,
        mode,
        settings.showZeroTotals,
        (id) => expandedRows[id] ?? expandAllRows ?? settings.expandTransactions,
        title,
        settings.sections
      ),
    [
      series,
      mode,
      settings.showZeroTotals,
      settings.expandTransactions,
      settings.sections,
      expandedRows,
      expandAllRows,
      title,
    ]
  );
  const dataRows = useMemo(() => rows.filter((item) => item.kind !== "Nav" && item.kind !== "Irr"), [rows]);
  const pinnedRows = useMemo(
    () => ({
      top: rows.filter((item) => item.kind === "Nav" && item.label === "Beginning NAV"),
      bottom: rows.filter((item) => (item.kind === "Nav" && item.label === "Ending NAV") || item.kind === "Irr"),
    }),
    [rows]
  );
  const handleToggleRow = useCallback(
    (row: CashFlowMatrixRow) => setExpandedRows((previous) => ({ ...previous, [row.id]: !row.expanded })),
    []
  );
  const handleAmountClick = useCallback(
    (row: CashFlowMatrixRow, cell: CashFlowAmountCell) => {
      if (cell.amount === undefined) {
        return;
      }
      const page = createCashFlowDrillDownPage(reportId, irr, {
        amount: cell.amount,
        amountFormatted: cell.formattedValue ?? "",
        description: row.label || "Cash flow",
        drillDownInfo: cell.drillDownInfo,
      });
      if (page) {
        dispatch(createDrillDownPageAction("add", page));
      }
    },
    [dispatch, irr, reportId]
  );
  const scheduleState = useMemo(() => {
    const hasExpandedRows = dataRows.some((item) => item.expandable && item.expanded);
    return {
      canDrillDown: !!reportId && !loading,
      onAmountClick: handleAmountClick,
      hasExpandableRows: dataRows.some((item) => item.expandable),
      hasExpandedRows,
      onToggleAllRows: () => {
        setExpandAllRows(!hasExpandedRows);
        setExpandedRows({});
      },
    };
  }, [dataRows, handleAmountClick, loading, reportId]);
  const dimensionFields = useMemo(() => fields.filter(isDimension), [fields]);
  const columns = useMemo(
    () =>
      getColumns({
        mode,
        series,
        dimensionFields,
        cashFlowFrequency: irr.config.cashFlowFrequency,
        onToggleRow: handleToggleRow,
      }),
    [mode, series, dimensionFields, irr.config.cashFlowFrequency, handleToggleRow]
  );

  if (series.some((column) => column.cell?.value != null && !column.cell.cashFlows)) {
    return <Alert severity="warning">{title}: cash flow details are unavailable. Refresh the report.</Alert>;
  }
  return (
    <Stack spacing={1} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      {irr.config.cashFlowFrequency && irr.config.cashFlowFrequency !== "Daily" && (
        <Typography variant="caption" color="textSecondary">
          IRR uses {irr.config.cashFlowFrequency.toLowerCase()} calculation dates. Transaction details keep their
          original dates.
        </Typography>
      )}
      <IntercomLauncherDependantElement mode="opacity">
        <ScrollableFlexContainer sx={{ minWidth: 0, minHeight: 0 }}>
          <CashFlowScheduleContext value={scheduleState}>
            <DataGridPremium
              rows={dataRows}
              loading={loading}
              slots={gridSlots}
              pinnedRows={pinnedRows}
              pinnedColumns={series.length > 1 ? splitPinnedColumns : undefined}
              columns={columns}
              getRowId={rowId}
              getRowClassName={rowClass}
              rowHeight={36}
              columnHeaderHeight={36}
              showCellVerticalBorder
              showColumnVerticalBorder
              hideFooter
              rowSelection={false}
              disableColumnReorder
              sx={sx}
            />
          </CashFlowScheduleContext>
        </ScrollableFlexContainer>
      </IntercomLauncherDependantElement>
    </Stack>
  );
};

export default CashFlowPreview;
